import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiX, FiPrinter, FiDownload, FiInfo } from 'react-icons/fi';
import toast from 'react-hot-toast';
import testService from '../../../services/testService';
import {
  buildQuestionPreviewData,
  buildQuestionPreviewFromPackRow,
  getPreviewApiErrorMessage,
  groupQuestionsForPreview,
  shouldHidePreviewAnswerFields,
} from './buildQuestionPreviewData';
import {
  downloadQuestionPaperPdf,
  parseInstructions,
  getEnovaLogoUrl,
} from './downloadQuestionPaperPdf';
import './questionPreview.css';

const sanitizeDownloadName = (name) =>
  (name || 'question-paper')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase() || 'question-paper';

const QuestionPreviewModal = ({ isOpen, onClose, packId, batchName, packSnapshot = null, assessment = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const previewContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !packId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const localPreview = buildQuestionPreviewFromPackRow(packSnapshot, assessment);
    if (localPreview) {
      setData(localPreview);
      setLoading(false);
      return undefined;
    }

    const fetchPreview = async () => {
      try {
        const res = await testService.getPackQuestions(packId);
        if (cancelled) {
          return;
        }
        if (res?.status === 'success' || res?.status === true) {
          const preview = buildQuestionPreviewData(res);
          if (!preview.questions.length) {
            setError('No questions found for this group.');
            return;
          }
          setData(preview);
        } else {
          setError(res?.message || 'Failed to load question paper');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch preview data:', err);
          setError(getPreviewApiErrorMessage(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [isOpen, packId, packSnapshot, assessment]);

  const sectionGroups = useMemo(() => {
    if (data?.sectionGroups?.length) {
      return data.sectionGroups;
    }
    if (!data?.questions?.length) return [];
    return groupQuestionsForPreview(data.sections, data.questions);
  }, [data]);

  const instructionItems = useMemo(
    () => parseInstructions(data?.template?.instructions),
    [data?.template?.instructions]
  );

  if (!isOpen) return null;

  const totalQuestions = data?.questions?.length ?? 0;

  const handleDownload = async () => {
    if (!previewContentRef.current || !data || downloading) {
      return;
    }

    setDownloading(true);
    const toastId = toast.loading('Preparing download...');

    try {
      const paperName = sanitizeDownloadName(data?.template?.name || batchName || data?.pack?.pack_name);
      const code = data?.pack?.id || packId || 'paper';
      await downloadQuestionPaperPdf(previewContentRef.current, `${paperName}-${code}.pdf`);
      toast.success('Question paper downloaded', { id: toastId });
    } catch (err) {
      console.error('Failed to download question paper:', err);
      toast.error('Failed to download question paper', { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white w-[min(96vw,calc(210mm+3.5rem))] h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="px-8 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Question Paper Preview</h3>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading || !!error || !data || downloading}
              title="Download question paper"
              className="p-2.5 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-all disabled:opacity-40 disabled:pointer-events-none"
            >
              <FiDownload size={18} className={downloading ? 'animate-pulse' : ''} />
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              title="Print question paper"
              className="p-2.5 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-all"
            >
              <FiPrinter size={18} />
            </button>
            <button onClick={onClose} className="p-2.5 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-all">
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="qpp-preview-viewport flex-1 overflow-y-auto">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <div className="w-10 h-10 border-4 border-slate-100 border-t-red-500 rounded-full animate-spin"></div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Preparing Preview...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <p className="text-sm font-bold text-red-500">{error}</p>
              <button onClick={onClose} className="btn-assessment btn-assessment-danger text-xs">Close</button>
            </div>
          ) : (
            <div
              ref={previewContentRef}
              className="qpp-root qpp-a4-document rounded-sm overflow-hidden print:shadow-none print:m-0 print:rounded-none"
            >
              <div className="qpp-brand-bar qpp-page-block">
                <img src={getEnovaLogoUrl()} alt="ENOVA Software and Hardware Solutions" className="qpp-brand-logo" />
              </div>
              <div className="qpp-page-block qpp-header">
                <h1 className="qpp-title">{data?.template?.name || 'Question Paper'}</h1>
                <p className="qpp-subtitle">{batchName || data?.pack?.pack_name}</p>

                <div className="qpp-stats">
                  <div className="qpp-stat">
                    <div className="qpp-stat-label">Duration</div>
                    <div className="qpp-stat-value">{data?.pack?.duration ?? '—'} Minutes</div>
                  </div>
                  <div className="qpp-stat">
                    <div className="qpp-stat-label">Total Marks</div>
                    <div className="qpp-stat-value">{data?.template?.total_marks ?? '—'} Marks</div>
                  </div>
                  <div className="qpp-stat">
                    <div className="qpp-stat-label">Questions</div>
                    <div className="qpp-stat-value">{totalQuestions} Total</div>
                  </div>
                </div>
              </div>

              <div className="qpp-page-block qpp-candidate">
                <div className="qpp-candidate-fields">
                  <div className="qpp-candidate-row qpp-candidate-row--split">
                    <div className="qpp-candidate-item">
                      <span className="qpp-field-label">Name:</span>
                      <span className="qpp-field-line" aria-hidden />
                    </div>
                    <div className="qpp-candidate-item">
                      <span className="qpp-field-label">Date:</span>
                      <span className="qpp-field-line" aria-hidden />
                    </div>
                  </div>
                  <div className="qpp-candidate-row">
                    <div className="qpp-candidate-item qpp-candidate-item--full">
                      <span className="qpp-field-label">Designation:</span>
                      <span className="qpp-field-line" aria-hidden />
                    </div>
                  </div>
                </div>
              </div>

              <div className="qpp-page-block qpp-instructions">
                <h4 className="qpp-instructions-title">
                  <FiInfo size={14} />
                  General Instructions
                </h4>
                <ul className="qpp-instructions-list">
                  {instructionItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="qpp-questions">
                {sectionGroups.length === 0 ? (
                  <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest py-8">No questions found for this group.</p>
                ) : (
                  sectionGroups.map(({ section, questions }, sIdx) => {
                    const sectionMarks =
                      Number(section.marks_per_question ?? section.marks) ||
                      Number(questions[0]?.marks) ||
                      1;

                    return (
                    <div key={section.id ?? sIdx}>
                      <div className="qpp-page-block qpp-section-header">
                        <div className="qpp-section-title-wrap">
                          <span className="qpp-section-badge">{sIdx + 1}</span>
                          <h2 className="qpp-section-title">{section.section_name}</h2>
                        </div>
                        <span className="qpp-section-meta">
                          {questions.length} Questions • {sectionMarks} Marks Each
                        </span>
                      </div>

                      <div className="qpp-question-list">
                        {questions.map((q, qIdx) => {
                          const hideAnswers = shouldHidePreviewAnswerFields(q, sectionMarks, section);
                          const isSubjective =
                            !hideAnswers &&
                            (!q.option_a || String(q.option_a).trim() === '');
                          const options = [q.option_a, q.option_b, q.option_c, q.option_d].filter(
                            (o) => o && String(o).trim() !== ''
                          );
                          const qMarks = Number(q.marks) || sectionMarks;

                          return (
                            <div key={q.id ?? `${sIdx}-${qIdx}`} className="qpp-page-block qpp-question">
                              <div className="qpp-question-header">
                                <span className="qpp-q-number">{qIdx + 1}.</span>
                                <p className="qpp-q-text">{q.question}</p>
                                <span className="qpp-q-marks">{qMarks} Mark{qMarks === 1 ? '' : 's'}</span>
                              </div>

                              {!hideAnswers && isSubjective && <div className="qpp-subjective-area" />}
                              {!hideAnswers && !isSubjective && options.length > 0 && (
                                <div className="qpp-options">
                                  {options.map((opt, i) => (
                                    <div key={i} className="qpp-option">
                                      <span className="qpp-option-letter">{String.fromCharCode(65 + i)}</span>
                                      <span className="qpp-option-text">{opt}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPreviewModal;
